import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

import CloseIcon from "~/content/icons/close.svg?react";

import "./Toast.css";

// Wait a moment after mount before sliding the toast in, so the user perceives
// it as feedback that arrives shortly after the page has loaded rather than as
// part of the page content.
const ENTER_DELAY_MS = 500;
// Keep in sync with the leave transition duration in Toast.css.
const EXIT_ANIMATION_MS = 250;

export function Toast({
  id,
  message,
  onDismiss,
  durationMs = 5000,
  className,
}) {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  // Mount hidden (translated above the viewport), then slide in after a short
  // delay so the entrance reads as a temporary, dismissible notification.
  useEffect(() => {
    const enter = setTimeout(() => setIsVisible(true), ENTER_DELAY_MS);
    return () => clearTimeout(enter);
  }, []);

  useEffect(() => {
    const autoDismiss = setTimeout(() => setIsLeaving(true), durationMs);
    return () => clearTimeout(autoDismiss);
  }, [durationMs]);

  useEffect(() => {
    if (!isLeaving) return;
    const finishExit = setTimeout(onDismiss, EXIT_ANIMATION_MS);
    return () => clearTimeout(finishExit);
  }, [isLeaving, onDismiss]);

  return (
    <output
      id={id}
      aria-live="polite"
      className={classNames("toast", className, {
        "toast--visible": isVisible && !isLeaving,
        "toast--leaving": isLeaving,
      })}
    >
      <span className="toast__check" aria-hidden="true">
        <svg viewBox="0 0 30 30" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <title>check</title>
          <circle cx="15" cy="15" r="13" fill="none" stroke="currentColor" strokeWidth="2" />
          <path
            d="M9 15.5l3.8 3.8L21 11"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
      <span className="toast__message">{message}</span>
      <button
        type="button"
        className="toast__close"
        aria-label={t("general.close")}
        onClick={() => setIsLeaving(true)}
      >
        <CloseIcon />
      </button>
    </output>
  );
}

Toast.propTypes = {
  id: PropTypes.string,
  message: PropTypes.string.isRequired,
  onDismiss: PropTypes.func.isRequired,
  durationMs: PropTypes.number,
  className: PropTypes.string,
};
